<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import type { ISelect, ISelectItem } from '@/components/ui/select/types'
  import { isLegitI18NKey } from '@/utils/i18n/isLegitKey'

  const { t } = useI18n()

  const props = withDefaults(defineProps<ISelect>(), {
    disabled: false,
    disableItemTranslation: false,
    srOnlyLabel: false
  })

  const selectId = useId()
  const labelId = `${selectId}-label`

  const placeholder = computed(() =>
    props.placeholder?.length ? props.placeholder : t('forms.select.placeholder.default')
  )

  const variant = computed(() => {
    if (props.disabled) return 'disabled'
    if (isOpen.value) return 'active'
    if (props.error) return 'error'

    return 'none'
  })
  const selectBase = computed(() =>
    cva(
      'inline-flex items-center justify-between rounded border border-border border-solid p-3 text-body-1 data-[placeholder]:text-secondary',
      {
        variants: {
          state: {
            active: 'border-bt-blue-ui',
            disabled: 'bg-grey',
            error: 'border-dark-error',
            none: ''
          }
        }
      }
    )({
      state: variant.value
    })
  )

  const isOpen = defineModel<boolean>('open', { default: false })
  const modelValue = defineModel<string>()

  function handleTranslation(item: ISelectItem) {
    if (props.disableItemTranslation) {
      return item.label
    }

    if (isLegitI18NKey(item.label)) {
      return t(item.label)
    }

    return item.label
  }
</script>

<template>
  <div class="flex flex-col">
    <Label
      v-if="label"
      :id="labelId"
      :for="selectId"
      class="mb-2 block text-subtitle-2"
      :class="{ 'sr-only': srOnlyLabel }"
    >
      {{ t(label) }}
    </Label>

    <SelectRoot
      v-model="modelValue"
      v-model:open="isOpen"
      :aria-label="label ? $t(label) : undefined"
      :disabled="disabled"
      :name="name"
    >
      <SelectTrigger
        :id="selectId"
        :aria-labelledby="label ? labelId : undefined"
        :class="selectBase"
      >
        <SelectValue
          :placeholder="placeholder"
          class="overflow-hidden text-ellipsis"
        />
        <div
          class="rotate-90 transform text-bt-blue-ui transition-all"
          :class="{ '!-rotate-90': isOpen }"
        >
          <Icon name="common/chevron-right" />
        </div>
      </SelectTrigger>

      <Label
        v-if="error"
        for="error"
        class="error mt-2 block transform text-dark-error transition-all text-body-3"
      >
        {{ t(error) }}
      </Label>
      <SelectContent
        body-lock
        prioritize-position
        class="z-10 h-full w-full transform rounded bg-white shadow-sm will-change-[opacity,transform] md:w-[--radix-select-trigger-width]"
        position="popper"
        :side-offset="error ? 34 : 8"
      >
        <div class="inline-flex items-center justify-between p-4 sm:hidden">
          <Label
            :for="selectId"
            class="block text-subtitle-1"
          >
            {{ t(label) }}
          </Label>
          <div
            class="cursor-pointer rounded-full bg-divider p-1"
            @click="isOpen = false"
          >
            <Icon
              class="text-bt-blue-ui"
              name="common/close"
            />
          </div>
        </div>

        <ScrollAreaRoot
          class="h-min overflow-y-auto overflow-x-hidden"
          type="auto"
        >
          <ScrollAreaViewport class="h-min max-h-full w-full gap-2.5 sm:max-h-[18.5rem] sm:p-2">
            <SelectItem
              v-for="item in items"
              :key="item.id"
              :value="item.id"
              :class="{ 'bg-divider': modelValue === item.id }"
              class="cursor-pointer px-4 py-3 sm:rounded md:hover:bg-divider"
            >
              <SelectItemText>
                <div class="flex items-center">
                  <NuxtImg
                    v-if="item.image"
                    class="mr-2 h-4"
                    width="24"
                    height="18"
                    loading="lazy"
                    :provider="item.isLocalImage ? undefined : 'storyblok'"
                    :src="item.image"
                    :alt="item.label"
                  />
                  <span> {{ handleTranslation(item) }}</span>
                </div>
              </SelectItemText>
            </SelectItem>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar
            class="flex touch-none select-none p-0.5 transition-colors duration-[160ms] ease-out data-[orientation=vertical]:w-2.5"
            orientation="vertical"
          >
            <ScrollAreaThumb
              class="relative flex-1 rounded-[0.625rem] bg-border before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[2.75rem] before:w-full before:min-w-[2.75rem] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"
            />
          </ScrollAreaScrollbar>
        </ScrollAreaRoot>
      </SelectContent>
    </SelectRoot>
  </div>
</template>

<style lang="scss" scoped>
  [data-radix-popper-content-wrapper] {
    @media (max-width: theme('screens.sm')) {
      position: fixed !important;
      width: 100%;
      height: 100%;
      transform: unset !important;
      max-width: unset !important;
      max-height: unset !important;
    }
  }

  :deep([data-radix-scroll-area-viewport]) {
    > div:first-of-type {
      @apply flex flex-col gap-2.5 #{!important};
    }
  }
</style>
